import { isApiError, isNetworkError } from "@/lib/api-error";
import { captureProductEvent } from "@/lib/posthog";
import { routePattern } from "./route";

/** Same codes as the new version, so a dashboard reads both. This version writes to PostHog only. */
export type ActionCode =
  | "assistente.abrir"
  | "assistente.card_acao"
  | "assistente.mensagem_enviar"
  | "assistente.resposta"
  | "busca.executar"
  | "busca.sem_resultado"
  | "candidato.adicionar"
  | "candidato.descartar"
  | "candidato.mover"
  | "erro.api"
  | "filtro.aplicar"
  | "filtro.limpar"
  | "lista.exportar"
  | "painel.abrir"
  | "painel.aba"
  | "projeto.confirmar_planejado"
  | "projeto.encerrar"
  | "projeto.renovar"
  | "vagas.vaga_abrir";

type EntityType = "freelancer" | "projeto" | "vaga";

export type EventResult = "ok" | "erro" | "cancelado";

export interface TrackOptions {
  entidade?: { tipo: EntityType; id?: string | number | null };
  contexto?: Record<string, unknown>;
  resultado?: EventResult;
  duracaoMs?: number;
}

export function trackEvent(action: ActionCode, options: TrackOptions = {}): void {
  const entityId = options.entidade?.id;
  captureProductEvent(action, {
    tela: routePattern(window.location.pathname),
    entidadeTipo: options.entidade?.tipo,
    entidadeId: entityId === undefined || entityId === null ? undefined : String(entityId),
    resultado: options.resultado,
    duracaoMs: options.duracaoMs,
    contexto: options.contexto,
  });
}

/** Free text becomes a code: "Não se aplica" becomes "nao_se_aplica". Never pass names or e-mails. */
export function toSlug(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const slug = value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_.:-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
  return slug || undefined;
}

/** `filtros` says what is on; this says what was turned on just now. */
export function filterChanges(previous: string[], current: string[]): { filtros_adicionados: string[]; filtros_removidos: string[] } {
  return {
    filtros_adicionados: current.filter((name) => !previous.includes(name)),
    filtros_removidos: previous.filter((name) => !current.includes(name)),
  };
}

/** An error shown to the person (the global TanStack Query handler). */
export function trackApiError(error: unknown): void {
  if (isApiError(error)) {
    let rota: string | undefined;
    try {
      rota = routePattern(new URL(error.url, window.location.origin).pathname);
    } catch {
      rota = undefined;
    }
    trackEvent("erro.api", {
      resultado: "erro",
      contexto: { status: error.status, metodo: error.method.toLowerCase(), rota, tipo: "http" },
    });
    return;
  }
  if (isNetworkError(error)) trackEvent("erro.api", { resultado: "erro", contexto: { status: 0, tipo: "rede" } });
}
